<template>
  <div id="app">
    <router-view />
    <!-- Global Language Switcher -->
    <div class="global-lang-switcher" @click="toggleLang">
      {{ currentLang === 'zh-CN' ? '中' : 'EN' }}
    </div>
    <!-- 版本更新弹窗 -->
    <version-update-dialog
      :visible.sync="showUpdateDialog"
      :current-version="versionInfo.currentVersion"
      :latest-version="versionInfo.latestVersion"
      :download-url="versionInfo.downloadUrl"
      :description="versionInfo.description"
      :force-update="versionInfo.forceUpdate"
      @update="handleUpdate"
      @cancel="handleCancelUpdate"
    />
  </div>
</template>

<script>
import VersionUpdateDialog from '@/components/VersionUpdateDialog.vue';

export default {
  name: 'App',
  components: {
    VersionUpdateDialog
  },
  data() {
    return {
      showUpdateDialog: false,
      versionInfo: {
        currentVersion: '',
        latestVersion: '',
        downloadUrl: '',
        description: '',
        forceUpdate: false
      }
    };
  },
  computed: {
    currentLang() {
      return this.$i18n.locale;
    }
  },
  mounted() {
    // 检查是否有版本更新信息
    this.checkVersionUpdate();
    // 监听版本更新事件
    window.addEventListener('version-update-available', this.checkVersionUpdate);
  },
  beforeDestroy() {
    window.removeEventListener('version-update-available', this.checkVersionUpdate);
  },
  methods: {
    toggleLang() {
      const newLang = this.currentLang === 'zh-CN' ? 'en-US' : 'zh-CN';
      this.$switchLang(newLang);
    },
    checkVersionUpdate() {
      // 只在 APP 环境检查
      if (!window.Capacitor) {
        return
      }
      const updateInfo = localStorage.getItem('version_update_info');
      if (updateInfo) {
        try {
          const info = JSON.parse(updateInfo);
          if (info.hasUpdate) {
            this.versionInfo = info;
            this.showUpdateDialog = true;
          }
        } catch (e) {
          console.error('解析版本信息失败:', e);
        }
      }
    },
    handleUpdate() {
      // 用户点击更新后清除版本信息
      localStorage.removeItem('version_update_info');
      this.showUpdateDialog = false;
    },
    handleCancelUpdate() {
      // 用户选择稍后再说，清除版本信息
      localStorage.removeItem('version_update_info');
    }
  }
};
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html, body, #app {
  height: 100%;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

/* ========== Global CSS Variables ========== */
:root {
  /* Brand Colors */
  --primary: #3498db;
  --primary-dark: #2980b9;
  --primary-light: #5dade2;
  
  --success: #2ecc71;
  --success-dark: #27ae60;
  --success-light: #58d68d;
  
  --warning: #f39c12;
  --warning-dark: #d68910;
  --warning-light: #f6b26b;
  
  --danger: #e74c3c;
  --danger-dark: #c0392b;
  --danger-light: #ec7063;
  
  --info: #3498db;
  --info-dark: #2980b9;
  --info-light: #5dade2;
  
  /* Text Colors */
  --text-primary: #2c3e50;
  --text-secondary: #666;
  --text-disabled: #999;
  --text-light: #bbb;
  
  /* Background Colors */
  --bg-body: #f5f7fa;
  --bg-white: #ffffff;
  --bg-light: #f9f9f9;
  
  /* Border Colors */
  --border-color: #eee;
  --border-light: #ddd;
  
  /* Shadows */
  --shadow-sm: 0 1px 3px rgba(0, 0, 0, 0.1);
  --shadow-md: 0 4px 12px rgba(0, 0, 0, 0.15);
  --shadow-lg: 0 8px 24px rgba(0, 0, 0, 0.2);
  
  /* Spacing */
  --spacing-xs: 4px;
  --spacing-sm: 8px;
  --spacing-md: 12px;
  --spacing-lg: 16px;
  --spacing-xl: 20px;
  
  /* Border Radius */
  --radius-sm: 4px;
  --radius-md: 8px;
  --radius-lg: 12px;
  
  /* Z-index Scale */
  --z-dropdown: 100;
  --z-sticky: 200;
  --z-badge: 300;
  --z-tooltip: 400;
  --z-modal: 1000;
  --z-toast: 2000;
}

/* Global Language Switcher - floating button */
.global-lang-switcher {
  position: fixed;
  bottom: 70px;
  right: 12px;
  z-index: 9999;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  font-size: 12px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  backdrop-filter: blur(4px);
  transition: all 0.2s;
  user-select: none;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
}

.global-lang-switcher:active {
  transform: scale(0.9);
  background: rgba(0, 0, 0, 0.9);
}

.global-lang-switcher:hover {
  background: rgba(0, 0, 0, 0.85);
}

</style>
